import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import {
  buildAppointmentStatusTranslationKey,
  buildTherapyTypeTranslationKey,
} from '@/constants/appointmentOptions';
import {
  DAY_VIEW_HOUR_HEIGHT_PIXELS,
  buildWorkingHoursSequence,
} from '@/constants/calendarConfiguration';
import { formatDateToFullWeekdayDate, formatDateToIsoDateOnly } from '@/utils/dateFormatting';
import {
  filterAppointmentsForIsoDate,
  sortAppointmentsByStartTime,
} from '../utils/appointmentFiltering';
import {
  assignAppointmentColumns,
  computeAppointmentHeightPixels,
  computeAppointmentTopOffsetPixels,
} from '../utils/appointmentLayout';
import {
  isAppointmentCompleted,
  resolveAppointmentStatusCode,
  resolveCaretakerDisplayName,
  isAppointmentCancelled,
  resolvePatientDisplayName,
} from '../utils/appointmentDisplay';
import { RescheduleRequestMarker } from '../RescheduleRequestMarker';
import { resolveAppointmentColorClasses } from '@/constants/appointmentColors';
import { ExternalEventOverlay } from '../ExternalEventOverlay';
import type { DayViewProps } from '../types';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';
import { useAssistantTargetCollection } from '@/components/assistant/uiActions/useAssistantTargetCollection';

export function DayView({
  currentDate,
  appointments,
  externalEvents,
  onAppointmentSelected,
}: DayViewProps) {
  const { t } = useTranslation();
  const buildAppointmentTargetRef = useAssistantTargetCollection(
    ASSISTANT_TARGET_KEY.dashboardAppointmentRow
  );

  const workingHours = buildWorkingHoursSequence();
  const gridHeightPixels = workingHours.length * DAY_VIEW_HOUR_HEIGHT_PIXELS;
  const currentIsoDate = formatDateToIsoDateOnly(currentDate);
  const dayAppointments = sortAppointmentsByStartTime(
    filterAppointmentsForIsoDate(appointments, currentIsoDate)
  );
  const patientFallbackText = t('appointments:patientFallback');
  const caretakerUnknownText = t('appointments:caretakerUnknown');
  const columnPlacements = assignAppointmentColumns(dayAppointments);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="py-4 px-6 border-b border-zooborder bg-zoovpalelavender bg-opacity-30">
        <h3 className="text-lg font-bold text-zooindigo">
          {formatDateToFullWeekdayDate(currentDate)}
        </h3>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="relative p-6 ml-16 mt-2 mb-4" style={{ height: `${gridHeightPixels}px` }}>
          <div className="absolute left-16 right-6 top-6">
            {workingHours.map(function renderHourRow(hour) {
              return (
                <div key={hour} className="h-24 border-b border-zooborder border-dashed relative">
                  <div className="absolute -left-16 -top-3 w-14 text-right pr-2">
                    <span className="text-xs font-bold text-zoomuted">{hour}:00</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="absolute left-16 right-6 top-6">
            <ExternalEventOverlay
              isoDate={currentIsoDate}
              externalEvents={externalEvents}
              hourHeightPixels={DAY_VIEW_HOUR_HEIGHT_PIXELS}
            />
          </div>

          <div className="absolute left-16 right-6 top-6 z-10">
            {dayAppointments.length > 0 ? (
              dayAppointments.map(function renderAppointmentCard(appointment) {
                const topOffsetPixels = computeAppointmentTopOffsetPixels(
                  appointment.startTime,
                  DAY_VIEW_HOUR_HEIGHT_PIXELS
                );
                const heightPixels = computeAppointmentHeightPixels(
                  appointment.startTime,
                  appointment.endTime,
                  DAY_VIEW_HOUR_HEIGHT_PIXELS
                );
                const caretakerName = resolveCaretakerDisplayName(
                  appointment,
                  caretakerUnknownText
                );
                const placement = columnPlacements[appointment.id];
                const columnCount = placement === undefined ? 1 : placement.columnCount;
                const columnIndex = placement === undefined ? 0 : placement.columnIndex;
                const columnWidthPercent = 100 / columnCount;
                const colorClasses = resolveAppointmentColorClasses(appointment.therapyType);
                const appointmentPatientName = resolvePatientDisplayName(
                  appointment,
                  patientFallbackText
                );
                return (
                  <button
                    key={appointment.id}
                    ref={buildAppointmentTargetRef(appointment.id)}
                    type="button"
                    aria-label={t('calendar:appointmentAriaLabel', {
                      patient: appointmentPatientName,
                      time: appointment.startTime,
                    })}
                    onClick={function editThisAppointment() {
                      onAppointmentSelected(appointment);
                    }}
                    className={cn(
                      'absolute text-left border border-zooborder border-l-4 rounded-2xl shadow-sm hover:border-zooprimary transition-all cursor-pointer group flex gap-3 p-3 overflow-hidden',
                      colorClasses.softBg,
                      colorClasses.accentBorderLeft,
                      isAppointmentCancelled(appointment) ? 'opacity-50' : ''
                    )}
                    style={{
                      top: `${topOffsetPixels}px`,
                      height: `${heightPixels}px`,
                      minHeight: '48px',
                      left: `calc(${columnIndex * columnWidthPercent}% + 4px)`,
                      width: `calc(${columnWidthPercent}% - 8px)`,
                    }}
                  >
                    <div className="w-16 flex-shrink-0 flex flex-col items-center justify-start border-r border-zooborder pr-3">
                      <div className="text-sm font-bold text-zooprimary leading-none mt-1">
                        {appointment.startTime}
                      </div>
                      <div className="text-[10px] text-zoomuted mt-1">
                        {appointment.endTime.length > 0 ? appointment.endTime : '--:--'}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-start">
                      <div className="flex justify-between items-start">
                        <div className="truncate pr-2">
                          <h4 className="font-bold text-zooindigo truncate leading-tight flex items-center gap-1">
                            <span className="truncate">{appointmentPatientName}</span>
                            <RescheduleRequestMarker appointment={appointment} />
                          </h4>
                          <p className="text-[10px] text-zoomuted truncate leading-tight mt-0.5">
                            {t('appointments:caretakerWithName', { name: caretakerName })}
                          </p>
                        </div>
                        <Badge
                          tone={isAppointmentCompleted(appointment) ? 'success' : 'neutral'}
                          uppercase
                          className="text-[9px] flex-shrink-0"
                        >
                          {t(
                            buildAppointmentStatusTranslationKey(
                              resolveAppointmentStatusCode(appointment)
                            )
                          )}
                        </Badge>
                      </div>
                      <div className="mt-1.5 flex items-center gap-3">
                        <div
                          className={cn(
                            'flex items-center gap-1 text-[10px] font-medium truncate',
                            colorClasses.accentText
                          )}
                        >
                          <Icon name="Activity" className="text-[12px]" />
                          {t(buildTherapyTypeTranslationKey(appointment.therapyType))}
                        </div>
                        {appointment.note.length > 0 && (
                          <div className="flex items-center gap-1 text-[10px] text-zoomuted">
                            <Icon name="FileText" className="text-[12px]" />
                            {t('appointments:noteBadge')}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="pt-32 text-center pointer-events-none">
                <Icon name="Calendar" className="text-4xl text-zoovpalelavender mb-4 mx-auto" />
                <p className="text-zoomuted font-medium">{t('calendar:noAppointmentsThatDay')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
